import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  ContractDocument,
  downloadContractDocument,
} from '@/services/contractsService'

interface ContractDocumentsProps {
  documents: ContractDocument[]
  loading?: boolean
  onDownload?: (documentId: string) => Promise<string | void>
}

const ContractDocuments: React.FC<ContractDocumentsProps> = ({
  documents,
  loading = false,
  onDownload,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const handleDownload = async (document: ContractDocument) => {
    if (downloadingId) return

    setDownloadingId(document._id)
    try {
      let downloadUrl: string | undefined

      // If onDownload is provided, use it (it may return a URL or handle download itself)
      if (onDownload) {
        const result = await onDownload(document._id)
        if (typeof result === 'string') {
          downloadUrl = result
        }
      } else {
        // Otherwise, fetch the URL directly from the API
        downloadUrl = await downloadContractDocument(document._id)
      }

      // If we have a URL, download it as a file
      if (downloadUrl) {
        await downloadFileFromUrl(
          downloadUrl,
          document.title || getDocumentName(document)
        )
      }
    } catch (error) {
      console.error('Failed to download document:', error)
    } finally {
      setDownloadingId(null)
    }
  }

  // Helper function to download file from URL
  const downloadFileFromUrl = async (url: string, filename: string) => {
    try {
      // Fetch the file as a blob to force download instead of opening in new tab
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      // Extract file extension from URL or use default
      let fileExtension = '.pdf'
      try {
        const urlPath = new URL(url).pathname
        const lastDotIndex = urlPath.lastIndexOf('.')
        if (lastDotIndex !== -1) {
          const extractedExtension = urlPath.substring(lastDotIndex)
          // Only use if it's a valid extension (has content after the dot)
          if (extractedExtension.length > 1) {
            fileExtension = extractedExtension
          }
        }
      } catch (e) {
        // If URL parsing fails, use default .pdf extension
        console.warn('Could not parse URL for extension, using .pdf', e)
      }

      const finalFilename = filename.includes('.')
        ? filename
        : `${filename}${fileExtension}`

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = finalFilename
      link.style.display = 'none'

      // Append to body, click, and remove
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
      }, 100)
    } catch (error) {
      console.error('Error downloading file:', error)
      // Fallback: open in new tab if download fails
      window.open(url, '_blank')
      throw error
    }
  }

  // Extract document name from fileKey or title
  const getDocumentName = (document: ContractDocument) => {
    if (document.fileKey) {
      const parts = document.fileKey.split('/')
      return parts[parts.length - 1] || document.title
    }
    return document.title
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Contract Documents
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : documents.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No contract documents available
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Document Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Created On
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    Download
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map(document => (
                  <TableRow key={document._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {document.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(document.createdAt), 'MM/dd/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(document)}
                        disabled={downloadingId === document._id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {downloadingId === document._id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default ContractDocuments
